import {ProcessFlowchartNodeDetailsType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {ProcessFlowchartNodeType} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/processFlowchartConfigEditorConstants';

import {DefaultProcessFlowchartNodeAdapter} from '../flowchart-viz/horizontal-flowchart/nodes/ProcessFlowchartNodeAdapter';
import doesFlowchartHaveRouterWithAndOrFunction from '../flowchart-viz/util/doesFlowchartHaveRouterWithAndOrFunction';
import {
  ProcessFlowchartForValidation,
  ProcessFlowchartValidationError,
} from '../validation/ProcessFlowchartForValidationTypes';
import {ProcessFlowchartDesignSpace} from '../validation/ProcessGraphBuilderConstants';
import {pushErrors} from '../validation/validateProcessFlowchartHelpers';
import {
  ensureLoopsContainTaskAndRouterNode,
  ensureLoopsHaveOneEntryPoint,
} from '../validation/validateProcessFlowchartRules';
import {
  ensureChainedRouterLimit,
  ensureNoChainedRouters,
  ensureNoMultipleRouterOnlyPathsBetweenTaskNodes,
  ensureRouterNodeConnectedToNonEndNode,
  ensureRouterNodeDestinationNodesAreNotSamePTDV,
  ensureRouterNodeHasOneIncomingEdge,
  ensureRouterNodeHasOneSourceSchema,
  ensureRouterNodeHasRouteConfiguredForEachEdge,
  ensureRouterNodeHasTwoOutgoingEdges,
} from '../validation/validateProcessRouterNodes';

/**
 * Adapter allowing stop nodes to be rendered in the processes editor.
 */
export class StopNodeAdapter extends DefaultProcessFlowchartNodeAdapter {
  nodeType: ProcessFlowchartNodeType = ProcessFlowchartNodeType.STOP_NODE;

  validateFlowchartForNodeType(
    flowchart: ProcessFlowchartForValidation,
    designSpace: ProcessFlowchartDesignSpace,
    didInitialFlowchartHaveChainedRouters: boolean,
    disconnectedNodeIds?: Array<string>
  ): Array<ProcessFlowchartValidationError> {
    let validationErrors: Array<ProcessFlowchartValidationError> = [];
    for (const node of flowchart.nodes) {
      if (node.nodeType === ProcessFlowchartNodeDetailsType.ROUTER_NODE_DETAILS) {
        validationErrors = pushErrors(
          validationErrors,
          ensureRouterNodeConnectedToNonEndNode(node, flowchart)
        );
      }
    }
    return validationErrors;
  }

  override maxIncomingEdges(isFlowchartOutput?: boolean): number {
    return 1;
  }
  override maxOutgoingEdges(isFlowchartOutput?: boolean): number {
    return 0;
  }
  override blockedAdjacentNodeTypes(): Array<ProcessFlowchartNodeType> {
    return [ProcessFlowchartNodeType.STOP_NODE, ProcessFlowchartNodeType.TASK_SCHEMA_NODE];
  }
}

/**
 * Adapter allowing router nodes to be rendered in the processes editor.
 */
export class RouterNodeAdapter extends DefaultProcessFlowchartNodeAdapter {
  nodeType: ProcessFlowchartNodeType = ProcessFlowchartNodeType.ROUTER_NODE;

  validateFlowchartForNodeType(
    flowchart: ProcessFlowchartForValidation,
    designSpace: ProcessFlowchartDesignSpace,
    didInitialFlowchartHaveChainedRouters: boolean,
    disconnectedNodeIds?: Array<string>
  ): Array<ProcessFlowchartValidationError> {
    let validationErrors: Array<ProcessFlowchartValidationError> = [];
    for (const node of flowchart.nodes) {
      if (node.nodeType === ProcessFlowchartNodeDetailsType.ROUTER_NODE_DETAILS) {
        if (disconnectedNodeIds == null || !disconnectedNodeIds.includes(node.id)) {
          validationErrors = pushErrors(
            validationErrors,
            ensureRouterNodeHasOneIncomingEdge(node, flowchart.edges)
          );
          validationErrors = pushErrors(
            validationErrors,
            ensureRouterNodeHasTwoOutgoingEdges(node, flowchart.edges)
          );
          validationErrors = pushErrors(validationErrors, ensureChainedRouterLimit(node, flowchart));
        }
        if (
          !didInitialFlowchartHaveChainedRouters ||
          (didInitialFlowchartHaveChainedRouters && doesFlowchartHaveRouterWithAndOrFunction(flowchart))
        ) {
          validationErrors = pushErrors(validationErrors, ensureNoChainedRouters(node, flowchart));
        }
        validationErrors = pushErrors(
          validationErrors,
          ensureRouterNodeDestinationNodesAreNotSamePTDV({node, flowchart, usingFlowchartFirstConfig: true})
        );
        validationErrors = pushErrors(
          validationErrors,
          ensureRouterNodeHasRouteConfiguredForEachEdge(node, flowchart)
        );
        validationErrors = pushErrors(
          validationErrors,
          ensureRouterNodeHasOneSourceSchema({
            node,
            flowchart,
            designSpace,
            usingFlowchartFirstConfig: true,
          })
        );
      }
      if (node.nodeType === ProcessFlowchartNodeDetailsType.TASK_NODE_DETAILS) {
        validationErrors = pushErrors(
          validationErrors,
          ensureNoMultipleRouterOnlyPathsBetweenTaskNodes({
            destinationNode: node,
            flowchart,
            usingFlowchartFirstConfig: true,
          })
        );
      }
      validationErrors = pushErrors(validationErrors, ensureLoopsContainTaskAndRouterNode(node, flowchart));
      validationErrors = pushErrors(validationErrors, ensureLoopsHaveOneEntryPoint(flowchart));
    }
    return validationErrors;
  }

  override maxIncomingEdges(isFlowchartOutput?: boolean): number {
    return 1;
  }

  override blockedAdjacentNodeTypes(): Array<ProcessFlowchartNodeType> {
    return super.blockedAdjacentNodeTypes();
  }
}
